import {
  erc20Abi,
  type Address,
  type EIP1193Provider,
  type PublicClient,
} from "viem";

import { publicClientFor } from "./wallet.js";

/**
 * Both sentinels we treat as "native gas token" — every other address
 * means an ERC-20 contract whose balance lives on-chain.
 *   - `0xEeee…EeEe` is the LI.FI / playground internal convention.
 *   - `0x0000…0000` is what the LI.FI catalog returns in `chain.nativeToken.address`.
 * Either should resolve to `getBalance` instead of `balanceOf`.
 *
 * The `isNative` boolean (sourced from the merged catalog `TokenDef`)
 * is the primary signal — the address fallback exists for legacy paths
 * that didn't propagate the flag.
 */
const NATIVE_ADDRESSES_LOWER = new Set<string>([
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
  "0x0000000000000000000000000000000000000000",
]);

/**
 * Read the balance of `owner` for `token` on `chainId`. Native tokens
 * go through `getBalance`; ERC-20s go through `readContract(balanceOf)`.
 *
 * Throws on RPC failures so callers can render an error chip.
 */
export async function readTokenBalance(args: {
  readonly provider: EIP1193Provider;
  readonly chainId: number;
  readonly token: Address;
  readonly owner: Address;
  readonly isNative?: boolean;
}): Promise<bigint> {
  const { provider, chainId, token, owner, isNative } = args;
  const client: PublicClient = publicClientFor(provider, chainId);

  const treatAsNative =
    isNative === true || NATIVE_ADDRESSES_LOWER.has(token.toLowerCase());

  if (treatAsNative) {
    return client.getBalance({ address: owner });
  }

  return client.readContract({
    address: token,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [owner],
  });
}
